import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';
import { EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX } from '../../../../../../../aumico-lib-core-fe/src/lib';
import { MappingItemsSelectionNode } from '../../../../interfaces/mapping-items-selection-node.interface';
import { ISuperTableComponentRowFormValue } from '../../../../interfaces/super-table-component-row-form-value.interface';
import { SuperTableMappingRowSettings } from '../../../../interfaces/super-table-mapping-row-settings.interface';
import { SuperTableMapper } from '../../../../mappers/super-table.mapper';

@UntilDestroy()
@Component({
    selector: 'super-table-mapping-row-settings',
    templateUrl: 'mapping-row-settings.component.html',
    styleUrls: ['mapping-row-settings.component.scss'],
})
export class SuperTableMappingRowSettingsComponent implements OnChanges {
    rowTitle: string = '';
    isRowTitleEdited = false;
    superTableMappingRowSettingsValue: SuperTableMappingRowSettings | undefined;
    existingSelectionItemsNodes: MappingItemsSelectionNode[] = [];

    @Input() matchingMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() allMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() balanceMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() incomeMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() existingRow: ISuperTableComponentRowFormValue | undefined;
    @Input() language: string | undefined;

    @Output() mappingRowSettings: EventEmitter<SuperTableMappingRowSettings | null> =
        new EventEmitter<SuperTableMappingRowSettings | null>();

    constructor(private readonly superTableMapper: SuperTableMapper) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['existingRow']?.currentValue) {
            if (this.existingRow.title != null && this.existingRow.title !== '') {
                this.rowTitle = this.existingRow.title;
                this.isRowTitleEdited = true;
            }
            const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(
                changes['existingRow'].currentValue,
                this.allMappingItemsSelectionTree,
            );
            this.existingSelectionItemsNodes = cloneDeep<MappingItemsSelectionNode[]>(matchingNodes);

            if (matchingNodes.length === EMPTY_ARRAY_LENGTH && this.existingRow.financialType == null) {
                throw new Error('financialType for row is not defined');
            }

            if (
                this.existingRow.financialType === TemplateType.BALANCE ||
                matchingNodes[FIRST_ELEMENT_INDEX]?.financialType == TemplateType.BALANCE
            ) {
                this.matchingMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                    this.balanceMappingItemsSelectionTree,
                );
            } else if (
                this.existingRow.financialType === TemplateType.INCOME ||
                matchingNodes[FIRST_ELEMENT_INDEX]?.financialType == TemplateType.INCOME
            ) {
                this.matchingMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                    this.incomeMappingItemsSelectionTree,
                );
            }

            this.matchingMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                this.superTableMapper.SelectMatchingNodes(
                    this.existingSelectionItemsNodes,
                    this.matchingMappingItemsSelectionTree,
                ),
            );
        }
    }

    onSelectedItemsChange(selectedNodes: MappingItemsSelectionNode[]): void {
        this.rowTitle = this.isRowTitleEdited ? this.rowTitle : this.getSelectedItemsConcatenatedNames(selectedNodes);
        this.superTableMappingRowSettingsValue = {
            selectedItems: selectedNodes,
            rowTitle: this.rowTitle,
        };
        this.mappingRowSettings.emit(this.superTableMappingRowSettingsValue);
    }

    onRowTitleChange(rowTitle: string): void {
        this.isRowTitleEdited = true;
        this.superTableMappingRowSettingsValue = {
            selectedItems: this.superTableMappingRowSettingsValue
                ? this.superTableMappingRowSettingsValue.selectedItems
                : [],
            rowTitle: rowTitle,
        };
        this.mappingRowSettings.emit(this.superTableMappingRowSettingsValue);
    }

    private getSelectedItemsConcatenatedNames(items: MappingItemsSelectionNode[]): string {
        let concatenatedName: string = '';
        for (const item of items) {
            if (!concatenatedName) concatenatedName = item.data.title || item.data.caption || item.data.name;
            else concatenatedName = concatenatedName + ', ' + (item.data.title || item.data.caption || item.data.name);
        }
        return concatenatedName;
    }
}
